import React from 'react';
import { useLoaderData, useMatches } from '@remix-run/react';
import { useIsomorphicLayoutEffect } from 'ahooks';
import type { ProfileNavigationItemProps } from '@archipro-design/aria';
import { ProfileNavigation, useStyles } from '@archipro-design/aria';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { getSubRoutes, getProfileNavItems } from './utils';
import * as S from './SubRouteNavigation.mobile.style';
import { useEditPermissions } from '~/modules/user';
import { useHydrated } from 'remix-utils';
import { parseURL } from '@archipro-website/top-navigation';
import type { ProfessionalDetailRootLoaderData } from '../../page/ProfessionalPage';

const scrollOptions: ScrollIntoViewOptions = {
    behavior: 'smooth',
    block: 'nearest',
    inline: 'center',
};

const SubRouteNavigationMobile: React.FC<{
    openEnquiryModal: () => void;
}> = ({ openEnquiryModal }) => {
    const professionalInitial = useProfessionalInitialRouteData();
    const { linkedProfiles } =
        useLoaderData<ProfessionalDetailRootLoaderData>();
    const matches = useMatches();
    const currentRoute = matches[matches.length - 1]?.pathname ?? '';
    const { basePath } = parseURL(currentRoute);
    const pathList = basePath.split('/').filter(Boolean);

    const currentTabPath = `/${
        pathList.length > 2 ? pathList[pathList.length - 1] : ''
    }`;

    const { ID: professionalId, LibraryLocations } = professionalInitial;

    const { canEditProducts } = useEditPermissions(professionalId);

    const menuItems = getSubRoutes(
        professionalInitial,
        canEditProducts,
        linkedProfiles?.linkedProfilesList
    );

    const navItems: Array<ProfileNavigationItemProps & { key: string }> =
        getProfileNavItems(menuItems, currentTabPath);
    const currentIndex = navItems.findIndex((item) => item.selected);
    const [activeIndex, setActiveIndex] = React.useState(currentIndex);

    const navRef = React.useRef<HTMLDivElement>(null);
    const hydrated = useHydrated();

    const { css } = useStyles({
        isDarkButton: currentTabPath === menuItems[0]?.link,
    });

    const hasLocation =
        LibraryLocations.some((l) => !l.LocationHidden) ||
        linkedProfiles?.linkedProfilesList.some((p) =>
            p.LibraryLocations.some((l) => !l.LocationHidden)
        );
    if (!hasLocation) {
        navItems.push({
            key: 'contact',
            children: <div>Contact</div>,
            selected: false,
            as: 'a',
            onClick: (e) => {
                e.preventDefault();
                openEnquiryModal();
            },
        });
    }

    React.useEffect(() => {
        setActiveIndex(currentIndex);
    }, [currentIndex]);

    useIsomorphicLayoutEffect(() => {
        if (hydrated) {
            const link = navRef.current?.querySelector(
                `a:nth-child(${activeIndex + 1})`
            );

            if (!link) {
                return;
            }
            link.scrollIntoView(scrollOptions);
        }
    }, [activeIndex, navRef, hydrated]);
    return (
        <ProfileNavigation
            ref={navRef}
            className={css(S.MobileNavTabWrapper)}
            items={navItems.map((item, i) => ({
                ...item,
                onClick:
                    item.onClick ||
                    ((e) => {
                        (e.target as Element).scrollIntoView(scrollOptions);
                        setActiveIndex(i);
                    }),
                selected: i === activeIndex,
            }))}
        />
    );
};

export default SubRouteNavigationMobile;
